<script setup lang="ts">
import { ref, onMounted } from 'vue'

const code = ref('')
const codeInput = ref<HTMLInputElement | null>(null)
const selectedIndex = ref(0)

const emit = defineEmits(['change'])

function changeCode(event: Event) {
  if (event.target === null) return

  const eventTarget = <HTMLInputElement>event.target

  // clear input if user attempt to remove any digit
  if ((<InputEvent>event).inputType === 'deleteContentBackward') {
    eventTarget.value = ''
  }

  // keep only numbers
  eventTarget.value = eventTarget.value.replace(/[^0-9]/, '')

  if (codeInput.value === null) return

  // if the cursor was not at the begining of the code, we want to replace
  // the currently selected number instead of adding a digit between existing numbers
  const selectionStart = <number>codeInput.value.selectionStart
  if (selectionStart) {
    eventTarget.value =
      eventTarget.value.substring(0, selectionStart) +
      eventTarget.value.substring(selectionStart + 1)
  }

  // we set the currently selected digit to the next one (sync with real input)
  selectedIndex.value = selectionStart

  // if there is more digits than expected, we trim the last ones
  if (eventTarget.value.length > 4) {
    eventTarget.value = eventTarget.value.substring(0, 4)
  }

  // if there is exactly 4 digits, we remove the focus to remove the keyboard on mobile
  if (eventTarget.value.length === 4) {
    selectedIndex.value = 4
    codeInput.value.blur()
  }

  // we finally store the code to be used in the next submit
  code.value = eventTarget.value

  emit('change', code.value)
}

function focus(index: number) {
  selectedIndex.value = index
  if (codeInput.value === null) return

  codeInput.value.focus()
  codeInput.value.selectionStart = index
  codeInput.value.selectionEnd = index

  selectedIndex.value = codeInput.value.selectionStart
}

onMounted(function () {
  if (codeInput.value === null) return
  codeInput.value.focus()
})
</script>

<template>
  <div class="flex relative justify-center">
    <input
      id="code"
      ref="codeInput"
      type="text"
      pattern="[0-9]*"
      inputmode="numeric"
      autocomplete="one-time-code"
      class="absolute opacity-0 w-px h-0 z-0"
      @input="changeCode"
    />
    <div class="flex gap-3 my-4">
      <span
        v-for="index in [0, 1, 2, 3]"
        :key="index"
        class="w-12 h-15 flex items-center justify-center border rounded-md text-3xl z-10 bg-white cursor-pointer transition-colors"
        :class="[
          selectedIndex === index ? 'border-brand ring-2 ring-brand/20' : 'border-gray-300',
          code.length > index ? 'text-gray-900' : 'text-transparent'
        ]"
        @click="focus(index)"
      >
        {{ code[index] || '' }}
      </span>
    </div>
  </div>
</template>
